import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import useCanonicalUrl from '../hooks/useCanonicalUrl'
import useFAQSchema from '../hooks/useFAQSchema'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import Input from '../atoms/Input'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import {
    setActiveTab,
    setGenBirthYear, calculateGeneration, clearGeneration,
    setLifeBirthDate, setLifeExpectancy, calculateLifeLived, clearLifeLived,
} from '../store/slices/ageSlice'
import '../pages/AgePage.css'

const GenerationCalculatorPage = ({ tools = [] }) => {
    useCanonicalUrl()
    useFAQSchema()
    const dispatch = useDispatch()
    const {
        activeTab,
        genBirthYear, genResult,
        lifeBirthDate, lifeExpectancy, lifeResult,
    } = useSelector(state => state.age)
    const [lastUpdated, setLastUpdated] = useState(null)

    useEffect(() => {
        setLastUpdated(
            new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        )
    }, [])

    useEffect(() => {
        dispatch(setActiveTab('generation'))
    }, [dispatch])

    useEffect(() => {
        document.title = 'Generation Calculator - Find Your Generation & Life in Weeks | EveryKits'
        const metaDescription = document.querySelector('meta[name="description"]')
        if (metaDescription) {
            metaDescription.setAttribute('content', 'Find out which generation you belong to (Gen Z, Millennial, Gen X, etc.) and visualize your life in weeks.')
        }
        const structuredData = {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Generation Calculator",
            "applicationCategory": "WebApplication",
            "operatingSystem": "All",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
            "description": "Free generation calculator and life in weeks visualizer.",
            "url": "https://everykits.com/generation-calculator/"
        }
        document.querySelectorAll('script[data-schema="software-application"]').forEach(el => el.remove());
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-schema', 'software-application');
        script.textContent = JSON.stringify(structuredData)
        document.head.appendChild(script)
        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, [])

    return (
        <ToolPageTemplate
            title="Generation Calculator"
            description="Find your generation and see your life in weeks"
            tools={tools}
            breadcrumbItems={[
                { name: 'Home', path: '/' },
                { name: 'Age Calculator', path: '/age/' },
                { name: 'Generation Calculator', path: '/generation-calculator/' },
            ]}
        >
            <div>
                <div className="age-tabs">
                    <button className={`age-tab ${activeTab === 'generation' ? 'active' : ''}`} onClick={() => dispatch(setActiveTab('generation'))}>Generation Finder</button>
                    <button className={`age-tab ${activeTab === 'life' ? 'active' : ''}`} onClick={() => dispatch(setActiveTab('life'))}>Life in Weeks</button>
                </div>

                {activeTab === 'generation' && (
                    <section className="age-calculator">
                        <div className="age-input-section">
                            <Input
                                type="number"
                                label="Birth Year"
                                value={genBirthYear}
                                onChange={(e) => dispatch(setGenBirthYear(e.target.value))}
                                min="1900"
                                max={new Date().getFullYear()}
                            />
                            <div className="age-buttons">
                                <Button onClick={() => dispatch(calculateGeneration())} disabled={!genBirthYear}>Find My Generation</Button>
                                <Button variant="secondary" onClick={() => dispatch(clearGeneration())}>Clear</Button>
                            </div>
                        </div>

                        {genResult && (
                            <ResultBox title="Your Generation">
                                <div className="age-item">
                                    <span className="age-label">Born in {genResult.year}, you belong to:</span>
                                    <span className="age-value">{genResult.generation}</span>
                                </div>
                            </ResultBox>
                        )}
                    </section>
                )}

                {activeTab === 'life' && (
                    <section className="age-calculator">
                        <div className="age-input-section">
                            <Input
                                type="date"
                                label="Date of Birth"
                                value={lifeBirthDate}
                                onChange={(e) => dispatch(setLifeBirthDate(e.target.value))}
                                max={new Date().toISOString().split('T')[0]}
                            />
                            <Input
                                type="number"
                                label="Life Expectancy (years)"
                                value={lifeExpectancy}
                                onChange={(e) => dispatch(setLifeExpectancy(e.target.value))}
                                min="1"
                                max="120"
                            />
                            <div className="age-buttons">
                                <Button onClick={() => dispatch(calculateLifeLived())} disabled={!lifeBirthDate}>Calculate</Button>
                                <Button variant="secondary" onClick={() => dispatch(clearLifeLived())}>Clear</Button>
                            </div>
                        </div>

                        {lifeResult && (
                            <ResultBox title="Your Life in Weeks">
                                <div className="age-details">
                                    <div className="age-item">
                                        <span className="age-label">Weeks Lived:</span>
                                        <span className="age-value">{lifeResult.livedWeeks.toLocaleString()}</span>
                                    </div>
                                    <div className="age-item">
                                        <span className="age-label">Weeks Remaining (est.):</span>
                                        <span className="age-value">{lifeResult.weeksRemaining.toLocaleString()}</span>
                                    </div>
                                    <div className="age-item">
                                        <span className="age-label">Total Weeks (at life expectancy):</span>
                                        <span className="age-value">{lifeResult.totalWeeks.toLocaleString()}</span>
                                    </div>
                                    <div className="age-item">
                                        <span className="age-label">Percentage of Life Lived:</span>
                                        <span className="age-value">{lifeResult.percentLived}%</span>
                                    </div>
                                </div>
                            </ResultBox>
                        )}
                    </section>
                )}
                <section className="tool-guide">
                    <div className="guide-section">
                        <h2>How to Use Generation Calculator</h2>
                        <div className="guide-steps">
                            <div className="step">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <h3>Enter Birth Year</h3>
                                    <p>Type in your birth year to instantly find out which generation you belong to.</p>
                                </div>
                            </div>
                            <div className="step">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <h3>Switch to Life in Weeks</h3>
                                    <p>Use the second tab to visualize your entire life in weeks based on your date of birth and estimated life expectancy.</p>
                                </div>
                            </div>
                            <div className="step">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <h3>Review Your Results</h3>
                                    <p>See your generation label or your percentage of life lived, weeks remaining, and total estimated weeks.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="guide-section">
                        <h2>Why Use This Tool</h2>
                        <p>Generational identity (Gen Z, Millennial, Gen X, Boomer) shapes cultural references, shopping habits, and social trends. Meanwhile, the "Life in Weeks" concept — popularized by writers like Tim Urban — offers a powerful visual perspective on time, helping people reflect on how they spend their limited years. Both tools combine curiosity with genuine self-reflection value.</p>
                    </div>
                    <div className="guide-section">
                        <h2>Frequently Asked Questions</h2>
                        <div className="faq-list">
                            <div className="faq-item">
                                <h3>What years define each generation?</h3>
                                <p>Generation Alpha starts 2013 onward, Gen Z is 1997-2012, Millennials are 1981-1996, Gen X is 1965-1980, and Baby Boomers are 1946-1964.</p>
                            </div>
                            <div className="faq-item">
                                <h3>What is "Life in Weeks"?</h3>
                                <p>It's a visual way to see how many weeks you've lived versus your estimated total lifespan, helping put time into perspective.</p>
                            </div>
                            <div className="faq-item">
                                <h3>How is life expectancy calculated?</h3>
                                <p>You enter your own estimate based on average regional life expectancy, and the tool calculates weeks lived and remaining accordingly.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="tool-guide">
                    <div className="guide-section">
                        <h2>Related Tools</h2>
                        <div className="related-tools">
                            <Link to="/age/" className="related-tool">
                                <div className="tool-icon">Age Calculator</div>
                                <div className="tool-description">Calculate your exact age from date of birth</div>
                            </Link>
                            <Link to="/eligibility-calculator/" className="related-tool">
                                <div className="tool-icon">Eligibility Calculator</div>
                                <div className="tool-description">Check exam and milestone age eligibility</div>
                            </Link>
                            <Link to="/retirement-age-calculator/" className="related-tool">
                                <div className="tool-icon">Retirement Age Calculator</div>
                                <div className="tool-description">See how your generation lines up with retirement planning</div>
                            </Link>
                            <Link to="/pet-age-calculator/" className="related-tool">
                                <div className="tool-icon">Pet Age Calculator</div>
                                <div className="tool-description">Another fun way to see age from a different perspective</div>
                            </Link>
                        </div>
                    </div>
                </section>

                <div className="last-updated">
                    <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
                </div>
            </div>
        </ToolPageTemplate>
    )
}

export default GenerationCalculatorPage